import { useEffect, useMemo, useState } from "react";
import ReactPhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./PhoneInput.css";
import {
  clampLocalDigits,
  maxLengthForDial,
  validatePhone,
} from "@/utils/phoneValidation";
import { EXACT_LOCAL_DIGITS } from "@/constants/phoneRules";

// Arab world → shown first, UAE (ae) at the top of the preferred list.
// react-phone-input-2 uses ISO2 country codes.
const ARAB_ISO2 = [
  "ae", "sa", "kw", "qa", "bh", "om",     // GCC (UAE first)
  "eg", "jo", "lb", "sy", "iq", "ye", "ps", // Levant + Egypt
  "ma", "dz", "tn", "ly", "sd",             // North Africa
  "so", "dj", "km", "mr",                   // Horn/West
];

// Arabic country names — only Arab list is localised; other countries keep English.
// Note: UAE name has a leading space so it sorts FIRST in react-phone-input-2's
// alphabetical ordering of preferredCountries (space 0x20 < Arabic 0x627+).
const AR_LOCALIZATION = {
  ae: " الإمارات",
  sa: "السعودية",
  kw: "الكويت",
  qa: "قطر",
  bh: "البحرين",
  om: "عُمان",
  eg: "مصر",
  jo: "الأردن",
  lb: "لبنان",
  sy: "سوريا",
  iq: "العراق",
  ye: "اليمن",
  ps: "فلسطين",
  ma: "المغرب",
  dz: "الجزائر",
  tn: "تونس",
  ly: "ليبيا",
  sd: "السودان",
  so: "الصومال",
  dj: "جيبوتي",
  km: "جزر القمر",
  mr: "موريتانيا",
};

/**
 * PhoneInput — drop-in replacement powered by react-phone-input-2.
 * Preserves the previous public API used across the app:
 *   value (string, digits only e.g. "966501234567")
 *   onChange (fn: receives digits-only string, no "+")
 *   placeholder, required, testId, disabled, defaultCountry ("966")
 *
 * Strict-length enforcement (SA/AE/KW/OM/QA):
 *   • Clamps digits on the fly to the exact allowed length.
 *   • Shows an inline error under the input when the user has typed but the
 *     local part doesn't match the exact expected length.
 *   • Never blocks re-selection of country — switching country re-clamps.
 */
export default function PhoneInput({
  value,
  onChange,
  placeholder = "رقم الجوال",
  required = false,
  testId = "phone-input",
  disabled = false,
  defaultCountry = "966",
  showValidation = true,
}) {
  const [touched, setTouched] = useState(false);
  // Map dial code → ISO2 for the initial country.
  const defaultISO2 = useMemo(() => {
    const map = {
      "966": "sa", "971": "ae", "965": "kw", "974": "qa", "973": "bh",
      "968": "om", "20": "eg", "962": "jo", "961": "lb", "963": "sy",
      "964": "iq", "967": "ye", "970": "ps", "212": "ma", "213": "dz",
      "216": "tn", "218": "ly", "249": "sd", "252": "so", "253": "dj",
      "269": "km", "222": "mr",
    };
    return map[String(defaultCountry)] || "sa";
  }, [defaultCountry]);

  // Inline validation state (only after the user has interacted).
  const check = validatePhone(value || "");
  const digitsOnly = String(value || "").replace(/\D/g, "");
  // Detect the current dial (longest prefix match from strict list) so we can
  // compute the exact per-country max length AND clamp reactively.
  const currentDial = useMemo(() => {
    const dials = Object.keys(EXACT_LOCAL_DIGITS).sort((a, b) => b.length - a.length);
    for (const d of dials) {
      if (digitsOnly.startsWith(d)) return d;
    }
    return "";
  }, [digitsOnly]);
  const localDigits = currentDial ? digitsOnly.slice(currentDial.length) : digitsOnly;
  const localLen = localDigits.length;
  const showError = showValidation && touched && localLen > 0 && !check.ok;

  // Hard-clamp guard: if the value ever exceeds the strict per-country max
  // (e.g. because react-phone-input-2's internal state got ahead of the
  // onChange callback), rewrite the value back down.
  useEffect(() => {
    if (!currentDial) return;
    const max = EXACT_LOCAL_DIGITS[currentDial];
    if (max === undefined) return;
    if (localLen > max) {
      onChange?.(`${currentDial}${localDigits.slice(0, max)}`);
    }
  }, [currentDial, localDigits, localLen, onChange]);

  // Dynamic maxLength on the DOM input — "+" prefix + dial digits + exact local.
  // Example SA: "+" (1) + "966" (3) + 9 local = 13.
  const domMaxLength = currentDial ? 1 + currentDial.length + maxLengthForDial(currentDial) : 1 + 3 + 15;

  return (
    <div
      className={`mz-phone-wrap ${showError ? "mz-phone-error" : ""}`}
      dir="ltr"
      data-testid={testId}
    >
      <ReactPhoneInput
        country={defaultISO2}
        value={value || ""}
        onChange={(v, country) => {
          // Clamp digits to strict/max length for the current country.
          const raw = String(v || "").replace(/\D/g, "");
          const dial = String(country?.dialCode || "").replace(/\D/g, "");
          if (dial && raw.startsWith(dial)) {
            const local = clampLocalDigits(dial, raw.slice(dial.length));
            onChange?.(`${dial}${local}`);
          } else {
            onChange?.(raw);
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        preferredCountries={ARAB_ISO2}
        localization={AR_LOCALIZATION}
        enableSearch
        disableSearchIcon={false}
        searchPlaceholder="ابحث عن الدولة…"
        searchNotFound="لا توجد نتائج"
        countryCodeEditable={false}
        autoFormat={false}
        inputProps={{
          required,
          "data-testid": `${testId}-number`,
          // Disable browser/OS autofill — booking phone must be typed each time.
          autoComplete: "off",
          autoCorrect: "off",
          spellCheck: false,
          name: `${testId}-${Math.random().toString(36).slice(2, 8)}`,
          onBlur: () => setTouched(true),
          // Dynamic maxLength — dial digits + exact strict-country max.
          maxLength: domMaxLength,
        }}
        containerClass="mz-phone-container"
        inputClass="mz-phone-input"
        buttonClass="mz-phone-button"
        dropdownClass="mz-phone-dropdown"
        searchClass="mz-phone-search"
      />
      {showError && (
        <div
          className="mz-phone-error-msg"
          data-testid={`${testId}-error`}
        >
          {check.reason}
        </div>
      )}
    </div>
  );
}
